/**
 * Browser side of the hosting API.
 *
 * One fetch wrapper so every call carries cookies, parses the JSON envelope,
 * and turns a failed request into an ApiError with the server's code — the
 * screens above can then react to EXPIRED or PENDING without parsing strings.
 */

export type AuthUser = {
  id: number;
  username: string;
  role: 'admin' | 'user';
  status: 'active' | 'pending' | 'suspended';
  plan: string;
  expiresAt: string | null;
  note: string;
  createdAt: string;
  lastLoginAt: string | null;
};

export type Access = {
  ok: boolean;
  code?: string;
  message?: string;
  daysLeft?: number | null;
};

export type AdminUser = AuthUser & {
  projects: number;
  projectBytes: number;
  expired: boolean;
};

export type Stats = {
  users: number;
  active: number;
  pending: number;
  suspended: number;
  admins: number;
  projects: number;
  projectBytes: number;
  storageDriver: string;
  registrationEnabled: boolean;
};

export type ProjectMeta = {
  id: string;
  name: string;
  bytes: number;
  createdAt: string;
  updatedAt: string;
};

export type Usage = {
  count: number;
  bytes: number;
  maxProjects: number;
  quotaBytes: number;
};

export type AuditEntry = {
  id: number;
  at: string;
  actor: string;
  action: string;
  detail: string;
};

export class ApiError extends Error {
  status: number;
  code: string;

  constructor(message: string, status: number, code: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

export async function api<T>(
  path: string,
  options: { method?: string; body?: unknown } = {},
): Promise<T> {
  const response = await fetch(path, {
    method: options.method ?? 'GET',
    credentials: 'same-origin',
    headers: options.body === undefined ? undefined : { 'Content-Type': 'application/json' },
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
  });

  let payload: unknown = null;
  const text = await response.text();
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      payload = null;
    }
  }

  if (!response.ok) {
    const record = (payload ?? {}) as { error?: string; code?: string };
    throw new ApiError(
      record.error ?? `Request failed (${response.status}).`,
      response.status,
      record.code ?? 'HTTP_ERROR',
    );
  }

  return payload as T;
}

export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const power = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
  const value = bytes / 1024 ** power;
  return `${value >= 10 || power === 0 ? Math.round(value) : value.toFixed(1)} ${units[power]}`;
}

export function formatDate(value: string | null | undefined): string {
  if (!value) return '—';
  const parsed = Date.parse(value);
  if (!Number.isFinite(parsed)) return '—';
  return new Date(parsed).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export function daysLeft(value: string | null | undefined): number | null {
  if (!value) return null;
  const parsed = Date.parse(value);
  if (!Number.isFinite(parsed)) return null;
  return Math.ceil((parsed - Date.now()) / 86400000);
}
